import {
  commandRequest,
  type UiCommandRequest,
} from '../../../agents/context/ui-command-registry.js';
import type { ChatContextRef, MeetingSnapshotItem } from '@govard/ai-chat-kit';
import {
  createCommandContextRef,
  type UiCommandKey,
} from '../../../agents/context/result-action-commands.js';

export type MyDayPriorityKind = 'meetings' | 'overdue_tasks' | 'risk_deals' | 'hot_leads';

export interface MyDayScenarioRequest {
  prompt: string;
  contextRefs?: ChatContextRef[];
  meetingSnapshot?: MeetingSnapshotItem[];
}

export interface MyDayScenarioMeeting {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  dealTitle?: string | null;
  contactName?: string | null;
}

export interface MyDayScenarioInput {
  meetings?: ReadonlyArray<MyDayScenarioMeeting>;
  overdueTasks: ReadonlyArray<{ id: string; title: string }>;
  riskDeals: ReadonlyArray<{ id: string; title: string }>;
  hotLeads: ReadonlyArray<{ id: string; title: string }>;
}

export const MY_DAY_PRIORITY_PROMPT_KEYS: Record<MyDayPriorityKind, string> = {
  meetings: 'crm.ai_chat.prompt.my_day_meetings',
  overdue_tasks: 'crm.ai_chat.prompt.my_day_overdue_tasks',
  risk_deals: 'crm.ai_chat.prompt.my_day_risk_deals',
  hot_leads: 'crm.ai_chat.prompt.my_day_hot_leads',
};

const MY_DAY_PRIORITY_COMMANDS: Record<MyDayPriorityKind, UiCommandKey> = {
  meetings: 'scenarioMeetings',
  overdue_tasks: 'scenarioOverdueTasks',
  risk_deals: 'scenarioRiskDeals',
  hot_leads: 'scenarioHotLeads',
};

const MAX_CONTEXT_REFS = 8;
export const MAX_MEETING_SNAPSHOT_ITEMS = 20;
const MAX_SNAPSHOT_FIELD_LENGTH = 200;
const MAX_SNAPSHOT_TIME_LENGTH = 64;

function toRefs(
  items: ReadonlyArray<{ id: string; title: string }>,
  type: 'task' | 'deal',
): ChatContextRef[] {
  return items.slice(0, MAX_CONTEXT_REFS).map((item) => ({
    type,
    id: item.id,
    label: item.title,
  }));
}

function trimMeetingSnapshotField(value: string, maxLength: number): string {
  return value.trim().slice(0, maxLength);
}

function toMeetingSnapshot(items: ReadonlyArray<MyDayScenarioMeeting>): MeetingSnapshotItem[] {
  return items.slice(0, MAX_MEETING_SNAPSHOT_ITEMS).map((meeting) => ({
    id: trimMeetingSnapshotField(meeting.id, MAX_SNAPSHOT_FIELD_LENGTH),
    title: trimMeetingSnapshotField(meeting.title, MAX_SNAPSHOT_FIELD_LENGTH),
    startsAt: trimMeetingSnapshotField(meeting.startsAt, MAX_SNAPSHOT_TIME_LENGTH),
    endsAt: trimMeetingSnapshotField(meeting.endsAt, MAX_SNAPSHOT_TIME_LENGTH),
    ...(meeting.dealTitle === null || meeting.dealTitle === undefined
      ? {}
      : { dealTitle: trimMeetingSnapshotField(meeting.dealTitle, MAX_SNAPSHOT_FIELD_LENGTH) }),
    ...(meeting.contactName === null || meeting.contactName === undefined
      ? {}
      : { contactName: trimMeetingSnapshotField(meeting.contactName, MAX_SNAPSHOT_FIELD_LENGTH) }),
  }));
}

export function getMyDayScenarioContextRefs(
  briefing: MyDayScenarioInput,
  kind: MyDayPriorityKind,
): ChatContextRef[] {
  switch (kind) {
    case 'meetings':
      return [];
    case 'overdue_tasks':
      return toRefs(briefing.overdueTasks, 'task');
    case 'risk_deals':
      return toRefs(briefing.riskDeals, 'deal');
    case 'hot_leads':
      return toRefs(briefing.hotLeads, 'deal');
  }
}

export function buildMyDayScenario(
  briefing: MyDayScenarioInput,
  kind: MyDayPriorityKind,
  t: (key: string) => string,
): MyDayScenarioRequest {
  const prompt = t(MY_DAY_PRIORITY_PROMPT_KEYS[kind]);
  const contextRefs = [
    createCommandContextRef(MY_DAY_PRIORITY_COMMANDS[kind], prompt),
    ...getMyDayScenarioContextRefs(briefing, kind),
  ];
  if (kind === 'meetings') {
    return { prompt, contextRefs, meetingSnapshot: toMeetingSnapshot(briefing.meetings ?? []) };
  }
  return { prompt, contextRefs };
}

function parseMeetingSnapshot(value: unknown): MeetingSnapshotItem[] | null {
  if (!Array.isArray(value) || value.length > MAX_MEETING_SNAPSHOT_ITEMS) {
    return null;
  }
  const snapshot: MeetingSnapshotItem[] = [];
  for (const item of value) {
    if (typeof item !== 'object' || item === null) {
      return null;
    }
    const record = item as Record<string, unknown>;
    const id = record.id;
    const title = record.title;
    const startsAt = record.startsAt;
    const endsAt = record.endsAt;
    if (
      typeof id !== 'string' ||
      typeof title !== 'string' ||
      typeof startsAt !== 'string' ||
      typeof endsAt !== 'string'
    ) {
      return null;
    }
    const readOptionalString = (key: string): { ok: true; value?: string } | { ok: false } => {
      const candidate = record[key];
      if (candidate === undefined) return { ok: true };
      if (typeof candidate !== 'string') return { ok: false };
      const trimmed = candidate.trim();
      if (trimmed === '') return { ok: true };
      return { ok: true, value: trimmed.slice(0, MAX_SNAPSHOT_FIELD_LENGTH) };
    };
    const dealResult = readOptionalString('dealTitle');
    const contactResult = readOptionalString('contactName');
    if (!dealResult.ok || !contactResult.ok) {
      return null;
    }
    const dealTitle = dealResult.value;
    const contactName = contactResult.value;
    snapshot.push({
      id: id.slice(0, MAX_SNAPSHOT_FIELD_LENGTH),
      title: title.slice(0, MAX_SNAPSHOT_FIELD_LENGTH),
      startsAt: startsAt.slice(0, MAX_SNAPSHOT_TIME_LENGTH),
      endsAt: endsAt.slice(0, MAX_SNAPSHOT_TIME_LENGTH),
      ...(dealTitle === undefined ? {} : { dealTitle }),
      ...(contactName === undefined ? {} : { contactName }),
    });
  }
  return snapshot;
}

/** `undefined` — поля нет; `null` — поле есть, но невалидно. */
function parseScenarioContextRefs(value: unknown): ChatContextRef[] | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (!Array.isArray(value)) {
    return null;
  }
  const contextRefs: ChatContextRef[] = [];
  for (const ref of value) {
    if (typeof ref !== 'object' || ref === null) {
      return null;
    }
    const item = ref as Record<string, unknown>;
    if (
      (item.type !== 'command' && item.type !== 'deal' && item.type !== 'task') ||
      typeof item.id !== 'string'
    ) {
      return null;
    }
    if (typeof item.label !== 'string') {
      return null;
    }
    contextRefs.push({ type: item.type, id: item.id, label: item.label });
  }
  return contextRefs;
}

export function parseMyDayScenarioRequest(value: unknown): MyDayScenarioRequest | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.prompt !== 'string' || record.prompt.trim() === '') {
    return null;
  }
  const contextRefs = parseScenarioContextRefs(record.contextRefs);
  if (contextRefs === null) {
    return null;
  }
  const withRefs = (base: {
    prompt: string;
    meetingSnapshot?: MeetingSnapshotItem[];
  }): MyDayScenarioRequest =>
    contextRefs === undefined || contextRefs.length === 0 ? base : { ...base, contextRefs };
  if (record.meetingSnapshot !== undefined) {
    const meetingSnapshot = parseMeetingSnapshot(record.meetingSnapshot);
    if (meetingSnapshot === null) {
      return null;
    }
    // The meetings branch must keep the scenario command ref: without it the
    // server never sees scenario_meetings and runs a plain prompt instead.
    return withRefs({ prompt: record.prompt, meetingSnapshot });
  }
  return withRefs({ prompt: record.prompt });
}

/** Typed action for persisted briefing priorities inside the chat only. */
export function buildChatMyDayCommand(
  briefing: MyDayScenarioInput,
  kind: MyDayPriorityKind,
  sourceMessageId: string,
): UiCommandRequest {
  if (kind === 'meetings')
    return commandRequest('scenarioMeetings', {
      sourceMessageId,
      meetingIds: (briefing.meetings ?? [])
        .slice(0, MAX_MEETING_SNAPSHOT_ITEMS)
        .map((meeting) => meeting.id),
    });
  const refs = getMyDayScenarioContextRefs(briefing, kind).slice(0, 7);
  return commandRequest(MY_DAY_PRIORITY_COMMANDS[kind], {
    sourceMessageId,
    [kind === 'overdue_tasks' ? 'taskIds' : 'dealIds']: refs.map((ref) => ref.id),
  });
}

/** Dashboard handoffs contain selection IDs, never briefing snapshots or prompt text. */
export function buildDashboardMyDayCommand(
  briefing: MyDayScenarioInput,
  kind: MyDayPriorityKind,
): UiCommandRequest {
  const source = { kind: 'dashboard_briefing' };
  if (kind === 'meetings')
    return commandRequest('scenarioMeetings', {
      source,
      meetingIds: (briefing.meetings ?? []).map((item) => item.id),
    });
  if (kind === 'overdue_tasks')
    return commandRequest('scenarioOverdueTasks', {
      source,
      taskIds: briefing.overdueTasks.map((item) => item.id),
    });
  return commandRequest(MY_DAY_PRIORITY_COMMANDS[kind], {
    source,
    dealIds: (kind === 'risk_deals' ? briefing.riskDeals : briefing.hotLeads).map(
      (item) => item.id,
    ),
  });
}
